import { useContext, useEffect } from 'react';
import homeHubApi from 'data/homeHub/api';
import * as redux from 'data/homeHub/redux';
import { ApiError, makeErrorFromApi } from 'lib/error';

import { setCountry, setUserType, setHomeId } from 'lib/tracking';
import { buildUrlStrict } from 'lib/url';
import { Member, Permissions, RegularMember } from 'types';
import { usePlan } from 'data/plan/usePlan';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import { useTranslation } from 'data/translation/useTranslation';
import { useError } from 'data/error/useError';
import redirect from 'lib/redirect';
import CSRFContext from 'data/csrf/context';
import { CSRFClient } from 'lib/csrf';

const DEFAULT_HOME_HUB_RETRIES = 10;

export const LOAD_HOME_HUB_ERROR_TYPE = 'LOAD_HOME_HUB_ERROR_TYPE';
export const loadHomeHubErrors = {
  NOT_FOUND: 'NOT_FOUND',
};

const setTrackingDataFromHomeHub = (home: redux.HomeHubData) => {
  // Only RegularMember can log in
  const current = home.members.find((m: Member) => m.isLoggedInUser) as
    | RegularMember
    | undefined;
  const role = current?.isMaster ? 'master' : 'sub';

  setCountry(current?.country ?? '');
  setUserType(role);
  setHomeId(home.homeId);
};

const resetTrackingData = () => {
  setUserType('');
  setHomeId('');
};

export const loadHomeHub =
  (dispatch: Dispatch, csrfClient: CSRFClient) => async (retries: number) => {
    dispatch(redux.loadHomeHubData());
    try {
      const data = await homeHubApi.loadHomeHub(retries, csrfClient);
      setTrackingDataFromHomeHub(data);
      dispatch(redux.setHomeHubData(data));
    } catch (error: any) {
      resetTrackingData();
      throw makeErrorFromApi(LOAD_HOME_HUB_ERROR_TYPE, error);
    }
  };

export type useHomeHubProps = {
  isLoadingHomeHub: boolean;
  homeHubData: redux.HomeHubData | null;
  permissions: Permissions | null;
  error?: ApiError;
};

const useHomeHub = ({
  withRetries,
}: {
  withRetries: boolean;
}): useHomeHubProps => {
  const { locale } = useTranslation();
  const dispatch = useDispatch();
  const homeHubState = useSelector(redux.selector);
  const { setError } = useError();
  const { planType } = usePlan();
  const { csrfClient } = useContext(CSRFContext);

  useEffect(() => {
    if (!homeHubState.data) {
      loadHomeHub(
        dispatch,
        csrfClient,
      )(withRetries ? DEFAULT_HOME_HUB_RETRIES : 1).catch(setError);
    }
  }, [dispatch, homeHubState.data, withRetries, setError, csrfClient]);

  useEffect(() => {
    const planTypeFromHomeHub =
      homeHubState?.data && homeHubState?.data.planType;

    if (planTypeFromHomeHub && planType !== planTypeFromHomeHub) {
      redirect(buildUrlStrict(planTypeFromHomeHub, 'HOME_HUB', locale));
    }
  }, [homeHubState, locale, planType]);

  return {
    isLoadingHomeHub: homeHubState.isLoading,
    homeHubData: homeHubState.data,
    permissions: homeHubState.data && homeHubState.data.permissions,
  };
};

export default useHomeHub;
